import { ActionIcon, Button, Input, Modal, Table, Title } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { IconSearch, IconX } from '@tabler/icons-react'
import { useDebounce, useMiddlewareClient } from '@/hooks'
import { AuditEvent } from '@medplum/fhirtypes'
import { createReference } from '@medplum/core'
import { Navigation } from '@/pages/Super/components'

type Payer = {
  payerId: string
  payerName: string
}

export function ProviderEnroll() {
  const [payers, setPayers] = useState<Payer[]>([])
  const [allPayers, setAllPayers] = useState<Payer[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [npi, setNpi] = useState<string | undefined>('-')
  const [taxId, setTaxId] = useState<string | undefined>('-')
  const [businessName, setBusinessName] = useState<string | undefined>('-')
  const [currentPayerId, setCurrentPayerId] = useState<string | undefined>()
  const [enrollLink, setEnrollLink] = useState<string | undefined>()
  const [enrolledIds, setEnrolledIds] = useState<(string | undefined)[]>([])
  const [hideEnrolled, setHideEnrolled] = useState(false)

  const debouncedSearchQuery = useDebounce(searchTerm, 200)
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()

  useEffect(() => {
    medplum.searchOne('Project', 'name=behuman').then((result) => {
      const npi = result?.secret?.find((item) => item.name === 'GENERAL_NPI')?.valueString
      const taxId = result?.secret?.find((item) => item.name === 'GENERAL_TAX_ID')?.valueString
      const name = result?.secret?.find((item) => item.name === 'GENERAL_NAME')?.valueString
      setNpi(npi)
      setTaxId(taxId)
      setBusinessName(name)
    })
    medplum.search('AuditEvent', 'type=provider-enroll&_count=1000').then((result) => {
      const ids = result.entry?.map((item) => item.resource?.outcomeDesc).filter(Boolean) || []
      setEnrolledIds(ids)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (debouncedSearchQuery.trim() === '') {
      setPayers(allPayers)
    } else {
      const filtered = allPayers.filter(
        (payer) =>
          payer.payerName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
          payer.payerId.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
      )
      setPayers(filtered)
    }
  }, [debouncedSearchQuery, allPayers])

  const retrievePayers = async () => {
    try {
      setLoading(true)
      const newPayers = await middleware.get('/insurance/get-payers')
      setPayers(newPayers.result)
      setAllPayers(newPayers.result)
    } finally {
      setLoading(false)
    }
  }

  const enroll = async (payerId: string) => {
    try {
      setCurrentPayerId(payerId)
      const link = await middleware.post('/insurance/enrole-provider', {
        payerId,
      })

      setEnrollLink(link.result)
      const user = medplum.getProfile()

      if (!user) return
      const auditEvent: AuditEvent = {
        resourceType: 'AuditEvent',
        type: {
          system: 'https://behuman.health/sys',
          code: 'provider-enroll',
          display: 'Provider Enroll',
        },
        agent: [
          {
            who: createReference(user),
            requestor: false,
          },
        ],
        source: {
          observer: createReference(user),
        },
        recorded: new Date().toISOString(),
        outcomeDesc: payerId.toString(),
      }
      await medplum.createResource(auditEvent)
      setEnrolledIds((prev) => [...prev, payerId])
    } finally {
      setCurrentPayerId(undefined)
    }
  }

  const payersList = hideEnrolled
    ? payers.filter((item) => !enrolledIds.includes(item.payerId))
    : payers

  return (
    <>
      <Navigation />
      <div className="px-6 py-4">
        <Title order={2} mb="md">
          Provider enroll
        </Title>

        {!npi && (
          <div className="text-red-700">{'{GENERAL_NPI}'} secret not found in Behuman project</div>
        )}
        {!taxId && (
          <div className="text-red-700">
            {'{GENERAL_TAX_ID}'} secret not found in Behuman project
          </div>
        )}
        {!businessName && (
          <div className="text-red-700">{'{GENERAL_NAME}'} secret not found in Behuman project</div>
        )}

        <Button loading={loading} onClick={retrievePayers}>
          Retrieve payers
        </Button>

        <Modal size="xl" opened={!!enrollLink} onClose={() => setEnrollLink(undefined)}>
          <iframe className="h-[800px] w-full" src={enrollLink} />
        </Modal>
        {allPayers.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 flex gap-2">
              <Input
                placeholder="Search by name or id"
                leftSection={<IconSearch />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                rightSection={
                  searchTerm ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                rightSectionPointerEvents={searchTerm ? 'all' : 'none'}
              />
              <Button
                variant="outline"
                color={!hideEnrolled ? 'purple' : 'blue'}
                onClick={() => setHideEnrolled(!hideEnrolled)}
              >
                {hideEnrolled ? 'Show enrolled' : 'Hide enrolled'}
              </Button>
            </div>
            <div className="relative max-h-[800px] max-w-[1250px] overflow-auto">
              <Table striped className="max-w-[1200px]">
                <Table.Thead className="sticky bg-white">
                  <Table.Tr>
                    <Table.Th>Payer ID</Table.Th>
                    <Table.Th>Payer Name</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody className="max-h-[800px] overflow-auto">
                  {payersList.map((payer) => {
                    const isEnrolled = enrolledIds.some((item) => item === payer.payerId)
                    return (
                      <Table.Tr>
                        <Table.Td width="10%">{payer.payerId}</Table.Td>
                        <Table.Td>{payer.payerName}</Table.Td>
                        <Table.Td width="20%">
                          <Button
                            loading={payer.payerId === currentPayerId}
                            onClick={() => enroll(payer.payerId)}
                            variant="outline"
                            {...(isEnrolled ? { color: 'orange' } : {})}
                          >
                            {isEnrolled ? 'Repeat Enroll' : 'Enroll'}
                          </Button>{' '}
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
